import * as Tone from 'tone';
import { getFx } from './fx/library.js';

// Mixer with channel strips.
//
// Signal flow per instrument channel:
//   instrument → channel.input (Gain) → channel.fader (Tone.Channel: vol/pan/mute/solo)
//                                          ├─→ master (dry)
//                                          └─→ sends → fx buses → master
//
// FX channels share the same shape but their input is fed by sends from other
// channels (and they may also have a wet effect node in the chain).

export function createMixer(bus) {
    const channels = new Map();
    const order = [];
    let master = null;

    let limiter = null;
    let masterComp = null;
    let masterMeter = null;
    let masterFft = null;

    function init() {
        if (master) return;
        // Master fader → glue compressor → limiter → destination.
        // The compressor "glues" the mix; the limiter prevents clipping.
        master = new Tone.Channel({ volume: -6, pan: 0 });
        masterComp = new Tone.Compressor({
            threshold: -18, ratio: 2.5, attack: 0.005, release: 0.12, knee: 8,
        });
        limiter = new Tone.Limiter(-1);
        masterMeter = new Tone.Meter();
        // 256-bin FFT is enough resolution for a small visual spectrum and
        // still cheap enough to refresh every animation frame.
        masterFft = new Tone.FFT(256);
        master.connect(masterComp);
        masterComp.connect(limiter);
        limiter.toDestination();
        // Tap the master fader output (pre-comp) so the meter reflects what
        // the channels are actually pushing in.
        master.connect(masterMeter);
        // Tap post-limiter so the spectrum reflects what the user actually hears.
        limiter.connect(masterFft);
    }

    function makeChannel({ id, name, color, isFx, effectNode }) {
        if (channels.has(id)) throw new Error(`channel "${id}" already exists`);

        // Backing storage for the mutable name/color exposed on the returned
        // channel object.
        let _name = name;
        let _color = color;

        const input = new Tone.Gain();
        // Built-in 3-band EQ on every channel. Default 0 dB on all bands == flat,
        // so it's transparent until the user turns a knob.
        const eq = new Tone.EQ3({ low: 0, mid: 0, high: 0, lowFrequency: 250, highFrequency: 3500 });
        const fader = new Tone.Channel({ volume: 0, pan: 0 });
        const meter = new Tone.Meter();
        // Tap the post-fader signal so the meter reads "what's being sent
        // out of this channel" — same point the master meter reads.
        fader.connect(meter);
        const sends = new Map();
        // Inserts live between the EQ and the channel's effect (if any) /
        // fader. Each entry is { id, libId, instance } where instance is the
        // factory output {node, params, dispose}.
        const inserts = [];

        input.connect(eq);
        fader.connect(master);

        // Rebuild the chain whenever the insert list changes. We disconnect
        // every node we manage (eq, each insert node, the optional effectNode)
        // and re-wire them in order: eq → inserts… → effectNode? → fader.
        function rebuildChain() {
            try { eq.disconnect(); } catch (_) {}
            for (const ins of inserts) try { ins.instance.node.disconnect(); } catch (_) {}
            if (effectNode) try { effectNode.disconnect(); } catch (_) {}
            let prev = eq;
            for (const ins of inserts) {
                prev.connect(ins.instance.node);
                prev = ins.instance.node;
            }
            if (effectNode) {
                prev.connect(effectNode);
                prev = effectNode;
            }
            prev.connect(fader);
        }
        rebuildChain();

        const ch = {
            id,
            // name and color are mutable so users can rename / recolor
            // channels in the mixer panel.
            get name() { return _name; },
            get color() { return _color; },
            isFx: !!isFx,
            input,
            fader,
            eq,
            setName(n) { _name = String(n); bus.emit('mixer:changed', { id, field: 'name' }); },
            setColor(c) { _color = String(c); bus.emit('mixer:changed', { id, field: 'color' }); },
            getEqLow: () => eq.low.value,
            setEqLow(v) { eq.low.value = v; bus.emit('mixer:changed', { id, field: 'eq.low' }); },
            getEqMid: () => eq.mid.value,
            setEqMid(v) { eq.mid.value = v; bus.emit('mixer:changed', { id, field: 'eq.mid' }); },
            getEqHigh: () => eq.high.value,
            setEqHigh(v) { eq.high.value = v; bus.emit('mixer:changed', { id, field: 'eq.high' }); },
            getVolume: () => fader.volume.value,
            setVolume(db) { fader.volume.value = db; bus.emit('mixer:changed', { id, field: 'volume' }); },
            getPan: () => fader.pan.value,
            setPan(p) { fader.pan.value = p; bus.emit('mixer:changed', { id, field: 'pan' }); },
            isMuted: () => fader.mute,
            setMuted(m) { fader.mute = !!m; bus.emit('mixer:changed', { id, field: 'mute' }); },
            isSolo: () => fader.solo,
            setSolo(s) { fader.solo = !!s; bus.emit('mixer:changed', { id, field: 'solo' }); },
            // Current peak level in dB (Tone.Meter returns dB; -Infinity at idle).
            getLevel: () => {
                const v = meter.getValue();
                return Array.isArray(v) ? Math.max(...v) : v;
            },
            // Post-fader send to an FX bus.
            addSend(fxId, dB = -Infinity) {
                const fx = channels.get(fxId);
                if (!fx) throw new Error(`fx "${fxId}" not found`);
                let s = sends.get(fxId);
                if (!s) {
                    s = new Tone.Volume(dB);
                    fader.connect(s);
                    s.connect(fx.input);
                    sends.set(fxId, s);
                } else {
                    s.volume.value = dB;
                }
                bus.emit('mixer:changed', { id, field: 'send', fxId });
                return s;
            },
            getSendLevel(fxId) {
                const s = sends.get(fxId);
                return s ? s.volume.value : -Infinity;
            },
            listSends() {
                return Array.from(sends.keys());
            },

            // Insert chain ─────────────────────────────────────────────────
            addInsert(libId) {
                const lib = getFx(libId);
                if (!lib) throw new Error(`unknown fx: ${libId}`);
                const instance = lib.create();
                const insertId = `${id}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,5)}`;
                inserts.push({ id: insertId, libId, instance });
                rebuildChain();
                bus.emit('mixer:changed', { id, field: 'inserts' });
                return insertId;
            },
            removeInsert(insertId) {
                const idx = inserts.findIndex(i => i.id === insertId);
                if (idx < 0) return;
                const [removed] = inserts.splice(idx, 1);
                rebuildChain();
                try { removed.instance.dispose(); } catch (_) {}
                bus.emit('mixer:changed', { id, field: 'inserts' });
            },
            listInserts() { return inserts.slice(); },
            clearInserts() {
                while (inserts.length) {
                    const [removed] = inserts.splice(0, 1);
                    try { removed.instance.dispose(); } catch (_) {}
                }
                rebuildChain();
                bus.emit('mixer:changed', { id, field: 'inserts' });
            },

            dispose() {
                input.dispose(); eq.dispose(); fader.dispose(); meter.dispose();
                sends.forEach(s => s.dispose());
                for (const ins of inserts) try { ins.instance.dispose(); } catch (_) {}
            },
        };
        channels.set(id, ch);
        order.push(id);
        bus.emit('mixer:added', ch);
        return ch;
    }

    return {
        init,
        getMaster: () => master,
        getMasterLevel: () => {
            if (!masterMeter) return -Infinity;
            const v = masterMeter.getValue();
            return Array.isArray(v) ? Math.max(...v) : v;
        },
        getMasterSpectrum: () => masterFft ? masterFft.getValue() : null,
        // Last node before AudioContext.destination — tap here for export.
        getOutputTap: () => limiter,
        addChannel: (opts) => makeChannel({ ...opts, isFx: false }),
        addFxChannel: (opts) => makeChannel({ ...opts, isFx: true }),
        get: (id) => channels.get(id),
        all: () => order.map(id => channels.get(id)),
        instruments: () => order.map(id => channels.get(id)).filter(c => !c.isFx),
        fx: () => order.map(id => channels.get(id)).filter(c => c.isFx),
    };
}
